import { keyframes } from '@emotion/react';
import { Alert, Box, Stack, Typography, Link as MuiLink } from '@mui/material';
import { useEffect, useState } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../core/contexts/AuthContext';
import { extractErrorMessage } from '../../core/services/api';
import { BrandMark } from '../../shared/components/BrandMark';
import { GoogleSignInButton } from '../../shared/components/GoogleSignInButton';

// Dark-panel palette mirrors website/assets/css/styles.css (:root, dark tokens)
// so the console's sign-in screen and the marketing site read as one brand.
const DARK = '#0b1220';
const DARK_2 = '#111b2e';
const DARK_LINE = 'rgba(255,255,255,0.12)';
const DARK_INK = '#eef2f8';
const DARK_MUTED = '#9fb0c6';
const BLUE = '#1f6feb';
const BLUE_LIGHT = '#5b9ef8';
const VIOLET = '#7c3aed';
const VIOLET_LIGHT = '#a78bfa';

const blink = keyframes`0%, 100% { opacity: 1; } 50% { opacity: .35; }`;
const floatGlow = keyframes`0%, 100% { transform: translate(0, 0); } 50% { transform: translate(-16px, 12px); }`;
const sloganIn = keyframes`from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); }`;

// Rotating marketing copy, drawn from docs/Vibelin-profile.md (Single Source of
// Truth, Impact Analysis, AI Assistants, From Idea to Implementation,
// Continuous Synchronization) — kept local since only this page uses it.
interface Slogan {
  prefix: string;
  emphasis: string;
  lead: string;
}
const SLOGANS: Slogan[] = [
  {
    prefix: 'One knowledge graph. ',
    emphasis: 'Every product decision.',
    lead: 'Market research, strategy, requirements, architecture and code — unified in a single living workspace.',
  },
  {
    prefix: 'See what a change will break — ',
    emphasis: 'before you make it.',
    lead: 'Every dependency traced and impact-analyzed, so nothing downstream breaks by surprise.',
  },
  {
    prefix: 'Specialized AI experts. ',
    emphasis: 'Full product context.',
    lead: 'Role-based AI assistants that reason across your entire lifecycle, not just the current document.',
  },
  {
    prefix: 'From idea to production, ',
    emphasis: 'faster.',
    lead: 'Validated strategy and documentation before the first line of code is written.',
  },
  {
    prefix: 'Nothing goes stale. ',
    emphasis: 'Everything stays in sync.',
    lead: 'Change a requirement and the knowledge graph updates everything connected to it — automatically.',
  },
];

const DISCIPLINES = ['Market Research', 'Strategy', 'Requirements', 'Architecture', 'Implementation'];

// Knowledge Intelligence Hub illustration: a centralized data/knowledge store
// (center) surrounded by what it holds (entities, relations, docs) and,
// further out, the product-lifecycle stages that feed it — each paired with
// the specialized AI agent (distinct skill icon) that operates at that stage.
// Hub is drawn as a database cylinder (top cap + curved body) rather than a
// plain circle, to read as a data store rather than just another node.
const HUB = { cx: 200, cy: 200, rx: 52, ry: 17, topY: 163, bottomY: 237, ringR: 76 };

const STAGE_NODES: { name: string; x: number; y: number; accent: string }[] = [
  { name: 'Market Research', x: 200, y: 50, accent: BLUE_LIGHT },
  { name: 'Strategy', x: 343, y: 154, accent: VIOLET_LIGHT },
  { name: 'Requirements', x: 288, y: 321, accent: '#3fbf7f' },
  { name: 'Architecture', x: 112, y: 321, accent: '#f2b84b' },
  { name: 'Implementation', x: 57, y: 154, accent: '#f472b6' },
];

const DATA_NODES: { name: string; x: number; y: number }[] = [
  { name: 'Data', x: 239, y: 147 },
  { name: 'Entities', x: 263, y: 220 },
  { name: 'Relations', x: 137, y: 220 },
  { name: 'Docs', x: 200, y: 266 },
];

const STAGE_PULSES = [
  { dur: '3.2s', begin: '0s' },
  { dur: '4.1s', begin: '0.6s' },
  { dur: '3.6s', begin: '1.2s' },
  { dur: '4.4s', begin: '0.3s' },
  { dur: '3.9s', begin: '1.8s' },
];

const ICON_STROKE = { fill: 'none', stroke: 'currentColor', strokeWidth: 1.3, strokeLinecap: 'round' as const, strokeLinejoin: 'round' as const };
const DATA_ICON_STROKE = { ...ICON_STROKE, strokeWidth: 1.1 };

/** Small skill-specific glyph for the AI agent paired with each stage — the
 * shape signals "different skills and tools", not a label. */
function AgentSkillIcon({ index }: { index: number }) {
  switch (index) {
    case 0: // Market Research — magnifying glass
      return (
        <>
          <circle cx={-1.4} cy={-1.4} r={3.6} {...ICON_STROKE} />
          <line x1={1.2} y1={1.2} x2={4.4} y2={4.4} {...ICON_STROKE} />
        </>
      );
    case 1: // Strategy — target
      return (
        <>
          <circle r={5} {...ICON_STROKE} />
          <circle r={2.4} {...ICON_STROKE} />
          <circle r={0.9} fill="currentColor" stroke="none" />
        </>
      );
    case 2: // Requirements — checklist
      return (
        <>
          <rect x={-5} y={-5} width={10} height={10} rx={2} {...ICON_STROKE} />
          <polyline points="-2.6,0.2 -0.6,2.2 3,-2.4" {...ICON_STROKE} />
        </>
      );
    case 3: // Architecture — layered blueprint
      return (
        <>
          <polyline points="-5,-4 0,-7 5,-4" {...ICON_STROKE} />
          <polyline points="-5,0 0,-3 5,0" {...ICON_STROKE} />
          <polyline points="-5,4 0,1 5,4" {...ICON_STROKE} />
        </>
      );
    default: // Implementation — code brackets
      return (
        <>
          <polyline points="-2,-6 -6,0 -2,6" {...ICON_STROKE} />
          <polyline points="2,-6 6,0 2,6" {...ICON_STROKE} />
        </>
      );
  }
}

/** Tiny pictograms for what the hub stores — decorative, shape carries the
 * meaning (data store, node cluster, chain link, page). */
function DataTypeIcon({ index }: { index: number }) {
  switch (index) {
    case 0: // Data — stacked store
      return (
        <>
          <ellipse cx={0} cy={-2.6} rx={4} ry={1.6} {...DATA_ICON_STROKE} />
          <path d="M -4 -2.6 L -4 2.6 A 4 1.6 0 0 0 4 2.6 L 4 -2.6" {...DATA_ICON_STROKE} />
        </>
      );
    case 1: // Entities — connected nodes
      return (
        <>
          <line x1={0} y1={-3} x2={-3} y2={2.5} {...DATA_ICON_STROKE} />
          <line x1={0} y1={-3} x2={3} y2={2.5} {...DATA_ICON_STROKE} />
          <line x1={-3} y1={2.5} x2={3} y2={2.5} {...DATA_ICON_STROKE} />
          <circle cx={0} cy={-3} r={1.1} fill="currentColor" stroke="none" />
          <circle cx={-3} cy={2.5} r={1.1} fill="currentColor" stroke="none" />
          <circle cx={3} cy={2.5} r={1.1} fill="currentColor" stroke="none" />
        </>
      );
    case 2: // Relations — chain link
      return (
        <>
          <ellipse cx={-1.8} cy={0} rx={2.4} ry={1.6} transform="rotate(-40 -1.8 0)" {...DATA_ICON_STROKE} />
          <ellipse cx={1.8} cy={0} rx={2.4} ry={1.6} transform="rotate(-40 1.8 0)" {...DATA_ICON_STROKE} />
        </>
      );
    default: // Docs — page with lines
      return (
        <>
          <rect x={-3.2} y={-4.2} width={6.4} height={8.4} rx={1} {...DATA_ICON_STROKE} />
          <line x1={-1.6} y1={-1.4} x2={1.6} y2={-1.4} {...DATA_ICON_STROKE} />
          <line x1={-1.6} y1={0.6} x2={1.6} y2={0.6} {...DATA_ICON_STROKE} />
          <line x1={-1.6} y1={2.6} x2={0.4} y2={2.6} {...DATA_ICON_STROKE} />
        </>
      );
  }
}

/** Decorative Knowledge Intelligence Hub illustration for the marketing
 * panel. Purely visual — the whole panel it lives in is aria-hidden. Center:
 * the hub, a centralized store of data/entities/relations/docs. Outer ring:
 * the product-lifecycle stages, each paired with its specialized AI agent. */
function KnowledgeHubIllustration({ animate }: { animate: boolean }) {
  return (
    <svg
      viewBox="-20 -20 440 440"
      style={{ width: '100%', height: 'auto', display: 'block', overflow: 'visible' }}
    >
      <defs>
        <radialGradient id="hubGlow" cx="50%" cy="50%" r="50%">
          <stop offset="0%" stopColor={BLUE_LIGHT} stopOpacity={0.55} />
          <stop offset="100%" stopColor={VIOLET_LIGHT} stopOpacity={0} />
        </radialGradient>
        <linearGradient id="hubFill" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor={BLUE} />
          <stop offset="100%" stopColor={VIOLET} />
        </linearGradient>
        <linearGradient id="hubEdgeGrad" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor={BLUE_LIGHT} />
          <stop offset="100%" stopColor={VIOLET_LIGHT} />
        </linearGradient>
      </defs>

      {/* Glow + slow rotating ring behind the hub */}
      <circle cx={HUB.cx} cy={HUB.cy} r={90} fill="url(#hubGlow)" />
      <circle cx={HUB.cx} cy={HUB.cy} r={HUB.ringR} fill="none" stroke={DARK_LINE} strokeWidth={1} strokeDasharray="1 9">
        {animate && (
          <animateTransform
            attributeName="transform"
            type="rotate"
            from={`0 ${HUB.cx} ${HUB.cy}`}
            to={`360 ${HUB.cx} ${HUB.cy}`}
            dur="24s"
            repeatCount="indefinite"
          />
        )}
      </circle>

      {/* Stage-to-stage mesh: each point also links to its neighbors on
          either side, not just back to the hub. */}
      <g stroke={`${BLUE_LIGHT}55`} strokeWidth={1} strokeDasharray="2 6" fill="none">
        {STAGE_NODES.map((s, i) => {
          const n = STAGE_NODES[(i + 1) % STAGE_NODES.length];
          return <line key={i} x1={s.x} y1={s.y} x2={n.x} y2={n.y} />;
        })}
      </g>

      {/* Hub → stored-data edges */}
      <g stroke={DARK_LINE} strokeWidth={1}>
        {DATA_NODES.map((d, i) => (
          <line key={i} x1={HUB.cx} y1={HUB.cy} x2={d.x} y2={d.y} />
        ))}
      </g>

      {/* Hub → lifecycle-stage edges, with data flowing along them */}
      <g stroke="url(#hubEdgeGrad)" fill="none" strokeWidth={1.5} opacity={0.55}>
        {STAGE_NODES.map((s, i) => (
          <line key={i} x1={HUB.cx} y1={HUB.cy} x2={s.x} y2={s.y} />
        ))}
      </g>
      {animate && (
        <g>
          {STAGE_NODES.map((s, i) => (
            <circle key={i} r={3} fill="#fff" opacity={0.85}>
              <animateMotion
                dur={STAGE_PULSES[i].dur}
                begin={STAGE_PULSES[i].begin}
                repeatCount="indefinite"
                path={`M${HUB.cx},${HUB.cy} L${s.x},${s.y}`}
              />
            </circle>
          ))}
        </g>
      )}

      {/* Hub — a database cylinder: curved body + top cap + two band seams,
          so the "centralized store" reading doesn't depend on the label. */}
      <path
        d={`M ${HUB.cx - HUB.rx} ${HUB.topY}
            L ${HUB.cx - HUB.rx} ${HUB.bottomY}
            C ${HUB.cx - HUB.rx} ${HUB.bottomY + HUB.ry * 1.7}, ${HUB.cx + HUB.rx} ${HUB.bottomY + HUB.ry * 1.7}, ${HUB.cx + HUB.rx} ${HUB.bottomY}
            L ${HUB.cx + HUB.rx} ${HUB.topY}`}
        fill="url(#hubFill)"
        stroke={DARK}
        strokeWidth={4}
        strokeLinejoin="round"
      />
      {[1 / 3, 2 / 3].map((f) => {
        const y = HUB.topY + (HUB.bottomY - HUB.topY) * f;
        return (
          <path
            key={f}
            d={`M ${HUB.cx - HUB.rx} ${y} C ${HUB.cx - HUB.rx} ${y + HUB.ry * 1.7}, ${HUB.cx + HUB.rx} ${y + HUB.ry * 1.7}, ${HUB.cx + HUB.rx} ${y}`}
            fill="none"
            stroke={DARK}
            strokeOpacity={0.35}
            strokeWidth={1.5}
          />
        );
      })}
      <ellipse cx={HUB.cx} cy={HUB.topY} rx={HUB.rx} ry={HUB.ry} fill="url(#hubFill)" stroke={DARK} strokeWidth={4} />
      <text x={HUB.cx} y={HUB.cy - 9} textAnchor="middle" fill={DARK_INK} fontSize={9} fontWeight={800} letterSpacing={0.3}>
        KNOWLEDGE
      </text>
      <text x={HUB.cx} y={HUB.cy + 3} textAnchor="middle" fill={DARK_INK} fontSize={9} fontWeight={800} letterSpacing={0.3}>
        INTELLIGENCE
      </text>
      <text x={HUB.cx} y={HUB.cy + 15} textAnchor="middle" fill={DARK_INK} fontSize={9} fontWeight={800} letterSpacing={0.3}>
        HUB
      </text>

      {/* What the hub stores */}
      {DATA_NODES.map((d, i) => (
        <g key={d.name}>
          <circle cx={d.x} cy={d.y} r={8} fill={DARK_2} stroke={DARK_LINE} strokeWidth={1} />
          <g transform={`translate(${d.x}, ${d.y})`} style={{ color: DARK_MUTED }}>
            <DataTypeIcon index={i} />
          </g>
        </g>
      ))}

      {/* Lifecycle stages, each with its specialized AI agent */}
      {STAGE_NODES.map((s, i) => {
        const labelAbove = s.y < HUB.cy;
        const labelY = labelAbove ? s.y - 20 : s.y + 26;
        const badgeDx = 16;
        const badgeDy = labelAbove ? 16 : -16;
        return (
          <g key={s.name}>
            <circle cx={s.x} cy={s.y} r={11} fill={s.accent} stroke={DARK} strokeWidth={3} />
            <text x={s.x} y={labelY} textAnchor="middle" fill={DARK_MUTED} fontSize={10} fontWeight={600}>
              {s.name}
            </text>
            <g transform={`translate(${s.x + badgeDx}, ${s.y + badgeDy})`}>
              <circle r={10} fill={DARK_2} stroke={s.accent} strokeWidth={1.5} />
              <g style={{ color: DARK_INK }}>
                <AgentSkillIcon index={i} />
              </g>
              <path d="M7,-9 L7.6,-7.4 9.2,-6.8 7.6,-6.2 7,-4.6 6.4,-6.2 4.8,-6.8 6.4,-7.4 Z" fill="#ffe08a" />
            </g>
          </g>
        );
      })}
    </svg>
  );
}

export function LoginPage() {
  const { isAuthenticated, loginWithGoogle, loading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [error, setError] = useState<string | null>(null);

  const [sloganIndex, setSloganIndex] = useState(0);
  const [reduceMotion, setReduceMotion] = useState(false);

  useEffect(() => {
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    setReduceMotion(mq.matches);
    const onChange = () => setReduceMotion(mq.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);

  useEffect(() => {
    if (reduceMotion) return;
    const id = window.setInterval(() => {
      setSloganIndex((i) => (i + 1) % SLOGANS.length);
    }, 6000);
    return () => window.clearInterval(id);
  }, [reduceMotion]);

  // Where to land once signed in: the page that sent the user here (e.g. an
  // onboarding wizard linked from the website), else the dashboard.
  const from = (location.state as { from?: { pathname: string } } | null)?.from?.pathname ?? '/';

  if (isAuthenticated) return <Navigate to={from} replace />;

  // A Google account we don't know yet still has to name its workspace
  // before an account exists — hand the signup token to that step.
  const onGoogleCredential = async (idToken: string) => {
    setError(null);
    try {
      const signup = await loginWithGoogle(idToken);
      if (signup) navigate('/complete-signup', { state: { ...signup, from }, replace: true });
      else navigate(from, { replace: true });
    } catch (err) {
      setError(extractErrorMessage(err));
    }
  };

  const slogan = SLOGANS[sloganIndex];

  return (
    <Box sx={{ minHeight: '100vh', display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' } }}>
      {/* ── Left: dark marketing panel — decorative, mirrors website/index.html's hero. ── */}
      <Box
        component="aside"
        aria-hidden="true"
        sx={{
          display: { xs: 'none', md: 'flex' },
          position: 'relative',
          overflow: 'hidden',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          gap: 5,
          px: 7,
          py: 8,
          color: DARK_INK,
          background: `
            radial-gradient(1100px 560px at 78% -8%, ${BLUE}2e, transparent 62%),
            radial-gradient(760px 460px at 8% 8%, ${VIOLET}26, transparent 60%),
            linear-gradient(180deg, ${DARK} 0%, ${DARK_2} 68%, #16233a 100%)`,
          '&::before': {
            content: '""',
            position: 'absolute',
            inset: 0,
            pointerEvents: 'none',
            backgroundImage:
              'linear-gradient(rgba(255,255,255,0.03) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.03) 1px, transparent 1px)',
            backgroundSize: '56px 56px',
            maskImage: 'radial-gradient(circle at 50% 30%, #000 0%, transparent 78%)',
          },
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            width: 520,
            height: 520,
            right: -140,
            top: -120,
            borderRadius: '50%',
            background: `radial-gradient(circle, ${BLUE}40, transparent 68%)`,
            filter: 'blur(30px)',
            animation: reduceMotion ? 'none' : `${floatGlow} 10s ease-in-out infinite`,
          }}
        />

        <Box sx={{ position: 'relative', zIndex: 1, width: '100%', maxWidth: 480 }}>
          <Stack direction="row" alignItems="center" spacing={1.2} sx={{ mb: 6 }}>
            <BrandMark />
            <Typography sx={{ fontWeight: 800, letterSpacing: '.14em', fontSize: 14, color: '#fff' }}>
              VIBELIN
            </Typography>
          </Stack>

          <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2.5 }}>
            <Box
              sx={{
                width: 6,
                height: 6,
                borderRadius: '50%',
                bgcolor: BLUE_LIGHT,
                boxShadow: `0 0 0 3px ${BLUE_LIGHT}33`,
                animation: reduceMotion ? 'none' : `${blink} 2.4s ease-in-out infinite`,
              }}
            />
            <Typography
              sx={{
                fontSize: 12,
                fontWeight: 600,
                letterSpacing: '.09em',
                textTransform: 'uppercase',
                color: DARK_MUTED,
              }}
            >
              Product Intelligence Platform
            </Typography>
          </Stack>

          <Box key={sloganIndex} sx={{ animation: reduceMotion ? 'none' : `${sloganIn} .5s ease` }}>
            <Typography
              sx={{
                fontSize: 'clamp(1.7rem, 2vw + 1rem, 2.3rem)',
                fontWeight: 800,
                lineHeight: 1.18,
                letterSpacing: '-0.02em',
                mb: 2,
                // Reserve room for the tallest slogan (2 lines) so shorter
                // ones don't shrink the block and shift everything below it.
                minHeight: 'calc(2 * 1.18em)',
              }}
            >
              {slogan.prefix}
              <Box
                component="span"
                sx={{
                  display: 'inline',
                  background: `linear-gradient(100deg, ${BLUE_LIGHT} 0%, #93b8ff 40%, ${VIOLET_LIGHT} 100%)`,
                  WebkitBackgroundClip: 'text',
                  backgroundClip: 'text',
                  color: 'transparent',
                }}
              >
                {slogan.emphasis}
              </Box>
            </Typography>
            <Typography sx={{ fontSize: '1.02rem', color: DARK_MUTED, maxWidth: '34rem', minHeight: 'calc(2 * 1.5em)' }}>
              {slogan.lead}
            </Typography>
          </Box>

          <Stack direction="row" spacing={0.8} sx={{ mt: 3 }}>
            {SLOGANS.map((_, i) => (
              <Box
                key={i}
                sx={{
                  width: 18,
                  height: 3,
                  borderRadius: 2,
                  bgcolor: i === sloganIndex ? BLUE_LIGHT : DARK_LINE,
                  transition: 'background-color .3s ease',
                }}
              />
            ))}
          </Stack>

          <Box sx={{ mt: 6 }}>
            <Box
              sx={{
                position: 'relative',
                p: 2.5,
                borderRadius: '16px',
                background: 'linear-gradient(160deg, rgba(255,255,255,0.07), rgba(255,255,255,0.02))',
                border: `1px solid ${DARK_LINE}`,
                backdropFilter: 'blur(8px)',
              }}
            >
              <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }}>
                <Box
                  sx={{
                    width: 7,
                    height: 7,
                    borderRadius: '50%',
                    bgcolor: '#3fbf7f',
                    boxShadow: '0 0 0 3px rgba(63,191,127,0.2)',
                    animation: reduceMotion ? 'none' : `${blink} 2.4s ease-in-out infinite`,
                  }}
                />
                <Typography
                  sx={{
                    fontSize: 11.5,
                    fontWeight: 600,
                    letterSpacing: '.09em',
                    textTransform: 'uppercase',
                    color: DARK_MUTED,
                  }}
                >
                  Live product knowledge graph
                </Typography>
              </Stack>
              <KnowledgeHubIllustration animate={!reduceMotion} />
            </Box>
          </Box>

          <Stack direction="row" flexWrap="wrap" useFlexGap spacing={1} sx={{ mt: 3.5 }}>
            {DISCIPLINES.map((d) => (
              <Box
                key={d}
                sx={{
                  px: 1.8,
                  py: 0.7,
                  borderRadius: 999,
                  fontSize: 13,
                  fontWeight: 500,
                  bgcolor: 'rgba(255,255,255,0.06)',
                  border: `1px solid ${DARK_LINE}`,
                  color: DARK_INK,
                }}
              >
                {d}
              </Box>
            ))}
          </Stack>
        </Box>
      </Box>

      {/* ── Right: bright panel — the actual sign-in form. ── */}
      <Box
        component="main"
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: 'background.default',
          px: { xs: 3, sm: 6 },
          py: 6,
          // Grid items default to min-width: auto, so a fixed-width descendant
          // (the Google button's iframe) would otherwise stop this column from
          // shrinking below it and overflow the viewport on narrow screens.
          minWidth: 0,
        }}
      >
        <Box sx={{ width: '100%', maxWidth: 400, minWidth: 0 }}>
          <Stack direction="row" alignItems="center" spacing={1.2} sx={{ mb: 5, display: { xs: 'flex', md: 'none' } }}>
            <BrandMark size={24} stroke={VIOLET} fill={BLUE} />
            <Typography sx={{ fontWeight: 800, letterSpacing: '.14em', fontSize: 13, color: 'text.primary' }}>
              VIBELIN
            </Typography>
          </Stack>

          <Typography variant="h4" component="h1" fontWeight={800} sx={{ mb: 0.75 }}>
            Welcome back
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
            Sign in to manage your tenant.
          </Typography>

          <Stack spacing={2.25}>
            {error && <Alert severity="error">{error}</Alert>}
            <GoogleSignInButton onCredential={onGoogleCredential} disabled={loading} />
            <Typography variant="body2" color="text.secondary" textAlign="center">
              Not invited yet?{' '}
              <MuiLink component="button" type="button" onClick={() => navigate('/request-access')}>
                Request access
              </MuiLink>
            </Typography>
          </Stack>
        </Box>
      </Box>
    </Box>
  );
}
